import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, CreditCard, ShieldAlert, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const PROGRAM_LABELS = {
  "7-9": "First Touch Academy · Ages 7–9",
  "10-12": "Elite Development · Ages 10–12",
  "13-15": "Performance Track · Ages 13–15",
};

export default function PayNow() {
  const { regId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api
      .get(`/registrations/${regId}/pay-info`)
      .then(({ data }) => setInfo(data))
      .catch(() => setError("We couldn't find this registration. Please use the exact link from your email, or contact us."));
  }, [regId]);

  const pay = async () => {
    setPaying(true);
    setError("");
    try {
      const { data } = await api.post(`/registrations/${regId}/pay`, { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-obsidian pt-28 pb-20">
      <section className="mx-auto max-w-xl px-5">
        <p className="font-body text-xs font-bold uppercase tracking-[0.35em] text-neon-green">ARD Midsouth Performance</p>
        <h1 className="mt-3 font-heading text-4xl uppercase leading-none text-white sm:text-5xl">
          Complete <span className="text-neon-green">Payment</span>
        </h1>

        <div data-testid="pay-page" className="mt-10 border border-white/10 bg-zinc-900 p-8">
          {!info && !error && (
            <div data-testid="pay-loading" className="flex items-center justify-center py-10">
              <Loader2 className="animate-spin text-neon-green" size={32} />
            </div>
          )}

          {error && !info && (
            <div data-testid="pay-error" className="text-center">
              <AlertTriangle size={40} strokeWidth={2.5} className="mx-auto text-yellow-400" />
              <p className="mt-4 font-body text-sm text-white/70">{error}</p>
              <Link to="/contact" className="mt-6 inline-block border border-neon-green/50 px-6 py-3 font-heading text-xs uppercase tracking-widest text-neon-green hover:bg-neon-green hover:text-black">
                Contact Us
              </Link>
            </div>
          )}

          {info && info.payment_status === "paid" && (
            <div data-testid="pay-paid" className="text-center">
              <CheckCircle2 size={44} strokeWidth={2.5} className="mx-auto text-neon-green" />
              <h2 className="mt-4 font-heading text-2xl uppercase text-white">You're All Set!</h2>
              <p className="mt-2 font-body text-sm text-white/60">
                Payment for <span className="text-neon-green">{info.player_name}</span> has been received. See you at training — Mondays & Thursdays, 5:00–6:00 PM.
              </p>
            </div>
          )}

          {info && info.payment_status !== "paid" && !info.waiver_signed && (
            <div data-testid="pay-waiver-needed" className="text-center">
              <ShieldAlert size={40} strokeWidth={2.5} className="mx-auto text-yellow-400" />
              <h2 className="mt-4 font-heading text-2xl uppercase text-white">One Step First</h2>
              <p className="mt-2 font-body text-sm text-white/60">
                Before paying, please sign the liability waiver for <span className="text-neon-green">{info.player_name}</span>. It takes two minutes, and you'll pay right after.
              </p>
              <Link
                to={`/waiver/${regId}`}
                data-testid="pay-goto-waiver"
                className="mt-6 inline-block bg-neon-green px-8 py-4 font-heading text-sm uppercase tracking-widest text-black"
              >
                Sign Waiver
              </Link>
            </div>
          )}

          {info && info.payment_status !== "paid" && info.waiver_signed && (
            <div>
              <div>
                <p className="font-body text-xs uppercase tracking-widest text-white/40">Player</p>
                <p data-testid="pay-player-name" className="mt-1 font-heading text-2xl uppercase text-white">{info.player_name}</p>
                <p className="mt-1 font-body text-sm text-white/60">{PROGRAM_LABELS[info.age_group] || info.age_group}</p>
              </div>
              <div className="mt-5 space-y-2 border-t border-white/10 pt-4 font-body text-sm">
                <div className="flex justify-between text-white/60">
                  <span>Winter Session 1 · {PROGRAM_LABELS[info.age_group] || info.age_group}</span>
                  <span>${info.amount != null ? info.amount.toFixed(0) : "—"}</span>
                </div>
                <div className="flex justify-between text-white/60">
                  <span>Online payment fee</span>
                  <span>${info.fee != null ? info.fee.toFixed(0) : "—"}</span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-2 font-heading text-lg text-white">
                  <span>Total</span>
                  <span data-testid="pay-amount" className="text-neon-green">${info.total != null ? info.total.toFixed(0) : "—"}</span>
                </div>
              </div>
              <p className="mt-4 font-body text-xs text-white/50">
                Winter Session 1 · Mondays & Thursdays · 5:00–6:00 PM · Nov 9 – Dec 7 (8 sessions)
              </p>
              {error && (
                <div data-testid="pay-submit-error" className="mt-4 border border-red-500/50 bg-red-500/10 px-4 py-3 font-body text-sm text-red-300">{error}</div>
              )}
              <button
                onClick={pay}
                disabled={paying}
                data-testid="pay-now-btn"
                className="mt-6 flex w-full items-center justify-center gap-2 bg-neon-green py-4 font-heading text-lg uppercase tracking-widest text-black transition-opacity disabled:opacity-60"
              >
                {paying ? <Loader2 className="animate-spin" size={22} /> : (<><CreditCard size={20} strokeWidth={2.5} /> Pay Now</>)}
              </button>
              <p className="mt-3 text-center font-body text-xs text-white/40">Secure card checkout powered by Stripe.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
